import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import { Plus, X } from 'lucide-react';
import * as React from 'react';

import type {
  IListAssignee,
  IListField,
  IListFieldCatalogue,
  ListFieldKind
} from '../../api/lists-contracts';
import {
  NO_PROBLEMS,
  NUMBER_KINDS,
  UNSAYABLE,
  asBox,
  criteriaPaths,
  fieldsByCode,
  forgetChanged,
  leafOf,
  leavesOf,
  newGroup,
  newLeaf,
  type CriteriaNode,
  type ICriteriaGroup,
  type ICriteriaLeaf,
  type ICriteriaRaw,
  type ICriteriaWords,
  type IPlacedCriteriaProblems
} from '../../list-criteria';
import {
  catalogueSections,
  operatorArity,
  operatorLabel,
  operatorsFor,
  optionLabel
} from '../../lists';
import { refusalMessage } from '../../refusal';
import { TagInput } from '../concern-criteria/tag-input';
import { DateLiteral } from './date-literal';
import { FieldPicker } from './field-picker';
import { accountIdsOf, isFacetPerson, personFacets } from './use-catalogue';
import { SuggestInput, SuggestTags } from './value-suggest';

/**
 * *Advanced…* — the filter drawn whole, composed by choosing (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3.4 and §4.2): rows of
 * *field · operator · value*, groups "all of these" / "any of these", nested without bound, a row
 * or a group added and removed — the concern criteria's builder shape (`ConditionBlock`) over this
 * grammar's own module. Lists draws it in a side sheet (`AdvancedFilterSheet`), Reports inline
 * (`CriteriaBuilder`), since there the conditions are the report's subject.
 *
 * **The controls are shaped by the catalogue and the operator mirror and judge nothing.** The
 * field list is the catalogue's filterable fields under their source's words; the operators those
 * its kind takes (`OPERATORS_BY_KIND`, a mirror), in words; the value control the one the operator
 * wants — an option's select, a checklist for "is any of", a number box, a date or a named day, two
 * of them for "between", none for "is blank". What is typed is held as typed and sent so
 * (`writeCriteria`), and survey-api's 400 lands on the row whose path it names (`[data-condition]`),
 * the text kept. **No node id goes to survey-api**: the module keeps them for the visit.
 *
 * A field of another kind starts the operator and value again; within a kind both stay. A stored
 * field, operator or option this app has no word for is offered as spelled, so opening a view
 * never changes its filter, and a node the words cannot say stays as one row, removable, kept
 * verbatim.
 */

/** The Select's value for "nothing chosen" — never a field code or an operator survey-api spells. */
const NONE = '#none';

/** An operator's empty value: none for the blank pair, a list, a pair, or `''`. */
function emptyValue(op: string): unknown {
  switch (operatorArity(op)) {
    case 'none':
      return undefined;
    case 'list':
      return [];
    case 'pair':
      return ['', ''];
    case 'one':
      return '';
  }
}

/** The operator a field's kind starts on: its first that takes a value, else its first. */
function startingOp(kind: ListFieldKind): string {
  const ops = operatorsFor(kind);
  return ops.find((op) => operatorArity(op) !== 'none') ?? ops[0] ?? '';
}

/** The context every row reads: the catalogue's fields, the words, the accounts *Assigned to*'s
 *  value is chosen from, the survey another person field's facets are read under, and where
 *  survey-api's refusals landed. The view's formatting rules are
 *  composed with the same rows (`formatting-sheet.tsx`), over a context of their own. */
export interface IBuilderContext {
  fields: ReadonlyMap<string, IListField>;
  sections: ReturnType<typeof catalogueSections>;
  words: ICriteriaWords;
  accounts?: readonly IListAssignee[];
  survey: Nullable<string>;
  paths: ReadonlyMap<string, string>;
  numbers: ReadonlyMap<string, number>;
  problemsOf: (nodeId: string) => readonly string[];
}

/**
 * The builder over a tree — every change answered whole through `onChange`. `placed` are
 * survey-api's refusals, by node; `accounts` the *Assigned to* picker's, for a person's value
 * (without them, a person's value is typed as an account id).
 */
export function CriteriaBuilder({
  root,
  catalogue,
  words,
  placed = NO_PROBLEMS,
  accounts,
  focusId,
  onChange
}: {
  root: ICriteriaGroup;
  catalogue: Nullable<IListFieldCatalogue>;
  words: ICriteriaWords;
  placed?: IPlacedCriteriaProblems;
  accounts?: readonly IListAssignee[];
  /** A node to bring into view — a nested group's chip opens the builder on it. */
  focusId?: string;
  onChange: (root: ICriteriaGroup) => void;
}) {
  const context = React.useMemo<IBuilderContext>(() => {
    const numbers = new Map(leavesOf(root).map((leaf, index) => [leaf.id, index + 1]));
    return {
      ...builderFields(catalogue, new Set(leavesOf(root).map((leaf) => leaf.field))),
      words,
      accounts,
      survey: catalogue?.survey ?? null,
      paths: criteriaPaths(root),
      numbers,
      problemsOf: (id) => placed.byNode.get(id) ?? []
    };
  }, [catalogue, root, words, accounts, placed]);

  const box = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!focusId) return;
    const node = box.current?.querySelector<HTMLElement>(`[data-node="${focusId}"]`);
    node?.scrollIntoView({ block: 'nearest' });
    // The value first where the row has a box for it — the field and the operator are chosen.
    (
      node?.querySelector<HTMLElement>('input:not([type="search"]), textarea') ??
      node?.querySelector<HTMLElement>('button, input, [role="combobox"]')
    )?.focus();
  }, [focusId]);

  return (
    <div ref={box} className="grid gap-2 text-[0.8125rem]">
      <Group group={root} context={context} onChange={onChange} />
      {placed.unplaced.length > 0 && (
        <div role="alert" className="grid gap-1 text-destructive">
          <p>The system refused these parts of the filter:</p>
          <ul className="grid gap-0.5">
            {placed.unplaced.map((problem, index) => (
              <li key={index}>{problem.reason}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Group({
  group,
  context,
  onChange,
  onRemove
}: {
  group: ICriteriaGroup;
  context: IBuilderContext;
  onChange: (group: ICriteriaGroup) => void;
  /** A nested group's "Remove group"; the root has none. */
  onRemove?: () => void;
}) {
  const nested = onRemove !== undefined;
  // A nested group is never empty — the parser refuses one — so it keeps a blank row; the root
  // may be (every row).
  const setChildren = (children: CriteriaNode[]) =>
    onChange({ ...group, children: nested && children.length === 0 ? [newLeaf()] : children });
  const setChild = (index: number, child: CriteriaNode) =>
    setChildren(group.children.map((held, at) => (at === index ? child : held)));
  const removeChild = (index: number) => setChildren(group.children.filter((_, at) => at !== index));

  return (
    <div
      data-group={context.paths.get(group.id)}
      data-node={group.id}
      className={cn('grid gap-2', nested && 'ml-1 border-l-2 border-border pl-4')}
    >
      <div className="flex flex-wrap items-center gap-2">
        {!nested && 'Show the rows where'}
        <Select value={group.op} onValueChange={(op) => onChange({ ...group, op: op as 'and' | 'or' })}>
          <SelectTrigger aria-label={nested ? 'Match in this group' : 'Match'} className="h-8 w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="and">all</SelectItem>
            <SelectItem value="or">any</SelectItem>
          </SelectContent>
        </Select>
        of these are true:
        {nested && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-muted-foreground"
            onClick={onRemove}
          >
            <X className="size-3.5" aria-hidden />
            Remove group
          </Button>
        )}
      </div>
      {context.problemsOf(group.id).map((reason, index) => (
        <p key={index} role="alert" className="text-destructive">
          {reason}
        </p>
      ))}
      {!nested && group.children.length === 0 && (
        <p className="text-muted-foreground">No condition yet — every row is shown.</p>
      )}
      {group.children.map((child, index) => {
        if (child.type === 'group') {
          return (
            <Group
              key={child.id}
              group={child}
              context={context}
              onChange={(next) => setChild(index, next)}
              onRemove={() => removeChild(index)}
            />
          );
        }
        if (child.type === 'raw') {
          return (
            <RawRow key={child.id} node={child} context={context} onRemove={() => removeChild(index)} />
          );
        }
        return (
          <ConditionRow
            key={child.id}
            leaf={child}
            context={context}
            onChange={(next) => setChild(index, next)}
            onRemove={() => removeChild(index)}
          />
        );
      })}
      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => setChildren([...group.children, newLeaf()])}
        >
          <Plus className="size-3.5" aria-hidden />
          Add a condition
        </Button>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          // A group inside "all" is usually an "any", and the reverse — the other word first.
          onClick={() => setChildren([...group.children, newGroup(group.op === 'and' ? 'or' : 'and')])}
        >
          <Plus className="size-3.5" aria-hidden />
          Add a group
        </Button>
      </div>
    </div>
  );
}

/** A stored node the words cannot say: one row, kept verbatim, removable. */
function RawRow({
  node,
  context,
  onRemove
}: {
  node: ICriteriaRaw;
  context: IBuilderContext;
  onRemove: () => void;
}) {
  return (
    <div data-condition={context.paths.get(node.id)} data-node={node.id} className="grid gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="italic">{UNSAYABLE}</span>
        <span className="text-muted-foreground">— kept as it was saved.</span>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Remove this condition"
          onClick={onRemove}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      <Problems reasons={context.problemsOf(node.id)} />
    </div>
  );
}

function Problems({ reasons }: { reasons: readonly string[] }) {
  return (
    <>
      {reasons.map((reason, index) => (
        <p key={index} role="alert" className="text-destructive">
          {reason}
        </p>
      ))}
    </>
  );
}

/** The catalogue's fields by code, and its filterable ones under their source's words — what a
 *  row's field list offers, a hidden one only where a row already names it (`held`). */
export function builderFields(catalogue: Nullable<IListFieldCatalogue>, held?: ReadonlySet<string>) {
  return {
    fields: fieldsByCode(catalogue),
    sections: catalogueSections(catalogue, (field) => field.filterable, held)
  };
}

/**
 * One row — *field · operator · value* — named "Condition n" in a filter and, with `noun`, "Rule n"
 * in the formatting rules, whose tone and boldness come after it (`children`). `hook` is the data
 * attribute the row carries its path in: `data-condition` for a filter's, `data-rule` for a rule's.
 */
export function ConditionRow({
  leaf,
  context,
  onChange,
  onRemove,
  noun = 'Condition',
  hook = 'data-condition',
  children
}: {
  leaf: ICriteriaLeaf;
  context: IBuilderContext;
  onChange: (leaf: ICriteriaLeaf) => void;
  onRemove: () => void;
  noun?: string;
  hook?: 'data-condition' | 'data-rule';
  children?: React.ReactNode;
}) {
  const number = context.numbers.get(leaf.id) ?? 0;
  const label = `${noun} ${number}`;
  const field = context.fields.get(leaf.field);
  const kind = field?.kind;
  const ops = kind ? operatorsFor(kind) : [];
  const offeredOps = leaf.op !== '' && !ops.includes(leaf.op) ? [...ops, leaf.op] : ops;
  const problems = context.problemsOf(leaf.id);

  const chooseField = (code: string) => {
    const next = context.fields.get(code);
    if (next && next.kind === kind) {
      onChange({ ...leaf, field: code });
      return;
    }
    const op = next ? startingOp(next.kind) : '';
    onChange({ ...leaf, field: code, op, value: emptyValue(op) });
  };

  const chooseOp = (op: string) => {
    // A value carries over where its shape does — one value to another, a list to a list.
    const arity = operatorArity(op);
    const keeps = leaf.op !== '' && arity === operatorArity(leaf.op) && arity !== 'none';
    onChange({ ...leaf, op, value: keeps ? leaf.value : emptyValue(op) });
  };

  return (
    <div {...{ [hook]: context.paths.get(leaf.id) }} data-node={leaf.id} className="grid gap-1">
      <div className="flex flex-wrap items-start gap-2">
        <FieldPicker
          label={`${label} — Field`}
          sections={context.sections}
          value={leaf.field}
          invalid={problems.length > 0}
          onChoose={chooseField}
          className="w-64"
        />
        <Select
          value={leaf.op === '' ? NONE : leaf.op}
          disabled={leaf.field === ''}
          onValueChange={(op) => chooseOp(op === NONE ? '' : op)}
        >
          <SelectTrigger aria-label={`${label} — Operator`} className="h-8 w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Choose</SelectItem>
            {offeredOps.map((op) => (
              <SelectItem key={op} value={op}>
                {operatorLabel(op)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ValueControl
          leaf={leaf}
          field={field}
          label={`${label} — Value`}
          invalid={problems.length > 0}
          accounts={context.accounts}
          survey={context.survey}
          personOf={context.words.personOf}
          onValue={(value) => onChange({ ...leaf, value })}
        />
        {children}
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={`Remove ${noun.toLowerCase()} ${number}`}
          onClick={onRemove}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      <Problems reasons={problems} />
    </div>
  );
}

/** The value the operator wants (§3.4) — the shape, never a judgment of what is typed. A person
 *  field read from its facets says a failed read beside the value, which is then typed. */
function ValueControl(props: IValueControlProps) {
  const { field, survey } = props;
  const { people, again } = useFacetPeople(isFacetPerson(field) ? survey : null, field?.code ?? '');
  const failed = people !== null && people !== 'reading' && 'failed' in people;
  if (!failed) return <ValueInput {...props} people={people} />;
  return (
    <span className="grid gap-1">
      <ValueInput {...props} people={null} facetFailed />
      <span role="alert" className="flex flex-wrap items-center gap-2 text-xs text-destructive">
        {people.failed}
        <Button type="button" variant="outline" className="h-7 px-2.5" onClick={again}>
          Read again
        </Button>
      </span>
    </span>
  );
}

interface IValueControlProps {
  leaf: ICriteriaLeaf;
  field: IListField | undefined;
  label: string;
  invalid: boolean;
  accounts?: readonly IListAssignee[];
  survey: Nullable<string>;
  /** The criteria's words — a held account the choices do not offer is labelled by them. */
  personOf?: (accountId: string) => string | undefined;
  onValue: (value: unknown) => void;
}

function ValueInput({
  leaf,
  field,
  label,
  invalid,
  accounts,
  survey,
  personOf,
  people,
  facetFailed = false,
  onValue
}: IValueControlProps & { people: 'reading' | Nullable<IChoice[]>; facetFailed?: boolean }) {
  const arity = leaf.op === '' ? null : operatorArity(leaf.op);
  if (arity === 'none') return null;
  if (arity === null || !field) {
    // No operator yet, or a field the catalogue does not offer: a stored value is kept and shown.
    const held = typeof leaf.value === 'string' || leaf.value === undefined;
    return held ? (
      <Input
        aria-label={label}
        disabled={arity === null}
        value={asBox(leaf.value)}
        onChange={(event) => onValue(event.target.value)}
        className="h-8 w-56"
      />
    ) : (
      <span className="self-center text-muted-foreground">{JSON.stringify(leaf.value)}</span>
    );
  }
  const { kind } = field;
  const dated = kind === 'date' || kind === 'datetime';
  if (people === 'reading') {
    return <span className="self-center text-muted-foreground">Reading the people…</span>;
  }
  const offered = people ?? (facetFailed ? null : choicesOf(field, accounts));
  const choices = offered && kind === 'person' ? withHeld(offered, leaf.value, personOf) : offered;
  // A text's value, a long text's too, is typed with the values the rows hold offered (SR-419).
  const suggested = kind === 'text' || kind === 'long_text' ? survey : null;

  if (arity === 'pair') {
    const pair = Array.isArray(leaf.value) ? leaf.value : ['', ''];
    const set = (at: number, one: string) =>
      onValue(pair.map((held, index) => (index === at ? one : held)));
    const end = (at: number, name: string) =>
      dated ? (
        <DateLiteral
          label={name}
          value={asBox(pair[at])}
          allowCycle={false}
          invalid={invalid}
          onChange={(one) => set(at, one)}
        />
      ) : (
        <NumberBox
          label={name}
          kind={kind}
          value={asBox(pair[at])}
          invalid={invalid}
          onChange={(one) => set(at, one)}
        />
      );
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {end(0, label)}
        and
        {end(1, `${label} — and`)}
      </span>
    );
  }

  if (arity === 'list') {
    const values = Array.isArray(leaf.value) ? leaf.value : [];
    if (choices) {
      return (
        <Checklist label={label} choices={choices} values={values} invalid={invalid} onChange={onValue} />
      );
    }
    if (suggested) {
      return (
        <SuggestTags
          label={label}
          values={values.map(asBox)}
          survey={suggested}
          field={field.code}
          typedOnly={kind === 'long_text'}
          invalid={invalid}
          onChange={onValue}
        />
      );
    }
    return <TagInput label={label} values={values.map(asBox)} onChange={onValue} invalid={invalid} />;
  }

  if (kind === 'bool') {
    const chosen = leaf.value === true ? 'true' : leaf.value === false ? 'false' : NONE;
    return (
      <Select value={chosen} onValueChange={(next) => onValue(next === NONE ? '' : next === 'true')}>
        <SelectTrigger aria-label={label} aria-invalid={invalid || undefined} className="h-8 w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Choose</SelectItem>
          <SelectItem value="true">Yes</SelectItem>
          <SelectItem value="false">No</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  if (choices) {
    const held = asBox(leaf.value);
    const stray = held !== '' && !choices.some((choice) => choice.code === held);
    const offered = stray ? [...choices, { code: held, label: held }] : choices;
    return (
      <Select value={held === '' ? NONE : held} onValueChange={(next) => onValue(next === NONE ? '' : next)}>
        <SelectTrigger aria-label={label} aria-invalid={invalid || undefined} className="h-8 w-56">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>Choose</SelectItem>
          {offered.map((choice) => (
            <SelectItem key={choice.code} value={choice.code}>
              {choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (dated) {
    return (
      <DateLiteral
        label={label}
        value={asBox(leaf.value)}
        allowCycle={leaf.op === 'since' || leaf.op === 'before'}
        invalid={invalid}
        onChange={onValue}
      />
    );
  }
  if (NUMBER_KINDS.includes(kind)) {
    return (
      <NumberBox label={label} kind={kind} value={asBox(leaf.value)} invalid={invalid} onChange={onValue} />
    );
  }
  if (suggested) {
    return (
      <SuggestInput
        label={label}
        value={asBox(leaf.value)}
        survey={suggested}
        field={field.code}
        typedOnly={kind === 'long_text'}
        invalid={invalid}
        onChange={onValue}
      />
    );
  }
  return (
    <Input
      aria-label={label}
      aria-invalid={invalid || undefined}
      autoComplete="off"
      value={asBox(leaf.value)}
      onChange={(event) => onValue(event.target.value)}
      className="h-8 w-56"
    />
  );
}

export interface IChoice {
  code: string;
  label: string;
  /** A second line under the label, searched with it — a firm group's members. */
  hint?: string;
}

/**
 * A person field's people as its facets name them (SR-408's D8) — for *Last edited by* or *Created
 * by*, whose values are advisors as well as staff, never the assignable accounts: `'reading'`, the
 * choices by email, `{ failed }` with the read's refusal in survey-api's words (gate 2's owner
 * ruling: said with "Read again", the value typed as an account id meanwhile — never the assignable
 * accounts offered in its place), or `null` (not such a field). Read afresh as the control mounts
 * and on `again`; survey-api's commonest 500.
 */
type FacetPeople = 'reading' | { failed: string } | Nullable<IChoice[]>;

function useFacetPeople(
  survey: Nullable<string>,
  field: string
): { people: FacetPeople; again: () => void } {
  const [people, setPeople] = React.useState<FacetPeople>(survey ? 'reading' : null);
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    if (survey === null) {
      setPeople(null);
      return;
    }
    const controller = new AbortController();
    setPeople('reading');
    personFacets(survey, field, controller.signal)
      .then((named) => setPeople([...named].map(([code, label]) => ({ code, label }))))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setPeople({ failed: refusalMessage(error, 'The people could not be read.') });
        }
      });
    return () => controller.abort();
  }, [survey, field, attempt]);
  return { people, again: () => setAttempt((n) => n + 1) };
}

/** A person's choices with every account the leaf holds that they do not offer, labelled by the
 *  criteria's words (an email a facet read narrowed to it gave) — else as spelled. */
function withHeld(
  choices: IChoice[],
  value: unknown,
  personOf?: (accountId: string) => string | undefined
): IChoice[] {
  const missing = accountIdsOf(value).filter((code) => !choices.some((choice) => choice.code === code));
  return missing.length === 0
    ? choices
    : [...choices, ...missing.map((code) => ({ code, label: personOf?.(code) ?? code }))];
}

/** What a value is chosen from: an option field's options, or *Assigned to*'s accounts by email —
 *  `null` where it is typed. */
function choicesOf(field: IListField, accounts?: readonly IListAssignee[]): Nullable<IChoice[]> {
  if (field.kind === 'enum' || field.kind === 'multi_enum') {
    return (field.options ?? []).map((option) => ({
      code: option.code,
      label: optionLabel(field, option.code)
    }));
  }
  if (field.kind === 'person' && accounts) {
    return accounts.map((account) => ({ code: account.accountId, label: account.email }));
  }
  return null;
}

/** A number's box — a dollar sign before money, a percent after; text held as typed. */
function NumberBox({
  label,
  kind,
  value,
  invalid,
  onChange
}: {
  label: string;
  kind: ListFieldKind;
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <span className="relative inline-flex items-center">
      {kind === 'money' && (
        <span aria-hidden className="pointer-events-none absolute left-2 text-muted-foreground">
          $
        </span>
      )}
      <Input
        aria-label={label}
        aria-invalid={invalid || undefined}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cn('h-8 w-40 tabular-nums', kind === 'money' && 'pl-5', kind === 'percent' && 'pr-6')}
      />
      {kind === 'percent' && (
        <span aria-hidden className="pointer-events-none absolute right-2 text-muted-foreground">
          %
        </span>
      )}
    </span>
  );
}

/** "Is any of": the choices as a checklist in a popover, a stored code the choices lack kept and
 *  offered as spelled. The search box shows past ten choices, or always where `searchable`. */
export function Checklist({
  label,
  choices,
  values,
  invalid,
  searchable = false,
  disabled = false,
  className,
  summary,
  wide = false,
  labelId,
  onChange
}: {
  label: string;
  choices: readonly IChoice[];
  values: readonly unknown[];
  invalid: boolean;
  searchable?: boolean;
  disabled?: boolean;
  /** The trigger's classes, over its size. */
  className?: string;
  /** What the trigger says of the codes ticked, in place of their labels. */
  summary?: (codes: string[]) => string;
  /** A popover wider than the trigger, its labels wrapped rather than cut — the finalists' firms. */
  wide?: boolean;
  /** A visible label's id: the trigger is then named by it and by what it says ("Firms 3 firms"),
   *  in place of `label` alone. */
  labelId?: string;
  onChange: (values: unknown[]) => void;
}) {
  const id = React.useId();
  const [search, setSearch] = React.useState('');
  const picked = values.map(asBox);
  const strays = picked.filter((code) => !choices.some((choice) => choice.code === code));
  const all: IChoice[] = [...choices, ...strays.map((code) => ({ code, label: code }))];
  const labelOf = (code: string) => all.find((choice) => choice.code === code)?.label ?? code;
  const needle = search.trim().toLowerCase();
  const shown =
    needle === ''
      ? all
      : all.filter((choice) => [choice.label, choice.hint ?? ''].some((text) => text.toLowerCase().includes(needle)));
  const said = picked.length === 0 ? 'Choose…' : summary ? summary(picked) : picked.map(labelOf).join(', ');

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={labelId ? undefined : label}
          aria-labelledby={labelId ? `${labelId} ${id}-said` : undefined}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          className={cn('h-8 w-56 justify-start truncate font-normal', className)}
        >
          <span id={`${id}-said`} className="truncate">
            {said}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={label}
        className={cn(
          'grid w-72 gap-2 p-3 text-[0.8125rem]',
          wide && 'w-auto min-w-[28rem] max-w-[36rem]'
        )}
      >
        {(searchable || all.length > 10) && (
          <Input
            type="search"
            aria-label="Search the choices"
            placeholder="Search…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-7"
          />
        )}
        <div role="group" aria-label="Choices" className="grid max-h-56 gap-1.5 overflow-auto">
          {shown.map((choice, index) => {
            const box = (
              <Checkbox
                id={choice.hint ? `${id}-${index}` : undefined}
                aria-describedby={choice.hint ? `${id}-${index}-hint` : undefined}
                checked={picked.includes(choice.code)}
                onCheckedChange={(next) =>
                  onChange(
                    next === true
                      ? [...picked, choice.code]
                      : picked.filter((code) => code !== choice.code)
                  )
                }
              />
            );
            // A hint describes the checkbox rather than naming it: its label is the choice alone.
            return choice.hint ? (
              <div key={choice.code} className={cn('flex gap-2', wide ? 'items-start' : 'items-center')}>
                {box}
                <span className="grid min-w-0">
                  <label htmlFor={`${id}-${index}`} className={wide ? 'break-words' : 'truncate'}>
                    {choice.label}
                  </label>
                  <span id={`${id}-${index}-hint`} className="line-clamp-2 text-xs text-muted-foreground">
                    {choice.hint}
                  </span>
                </span>
              </div>
            ) : (
              <label key={choice.code} className={cn('flex gap-2', wide ? 'items-start' : 'items-center')}>
                {box}
                <span className={cn('min-w-0', wide ? 'break-words' : 'truncate')}>{choice.label}</span>
              </label>
            );
          })}
          {shown.length === 0 && <p className="text-muted-foreground">No choice matches.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * *Advanced…* in a side sheet — Lists' door to the whole tree. It holds a draft from its opening:
 * the rows are read again only on **Apply**, since an unfinished row would be a 400 on every key.
 * survey-api's refusals of the tree last applied come back through `placed` (the caller reopens the
 * sheet on them), and follow their rows as the draft moves (`forgetChanged`).
 */
export function AdvancedFilterSheet({
  open,
  onOpenChange,
  root,
  catalogue,
  words,
  placed = NO_PROBLEMS,
  accounts,
  focusId,
  addField,
  onApply
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  root: ICriteriaGroup;
  catalogue: Nullable<IListFieldCatalogue>;
  words: ICriteriaWords;
  placed?: IPlacedCriteriaProblems;
  accounts?: readonly IListAssignee[];
  focusId?: string;
  /** A field chosen under *Filters* (SR-412): the sheet opens with a new condition on it after the
   *  tree's, its operator the kind's first, the focus on it. */
  addField?: string;
  onApply: (root: ICriteriaGroup) => void;
}) {
  const [draft, setDraft] = React.useState(root);
  const [problems, setProblems] = React.useState(placed);
  const [added, setAdded] = React.useState<string | undefined>(undefined);
  // The draft starts again from the tree on each opening, and only then: a parent re-rendering
  // under the open sheet (a poll, a fresh `placed` object) must not take the rows being typed.
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      const kind = addField ? fieldsByCode(catalogue).get(addField)?.kind : undefined;
      const op = kind ? startingOp(kind) : '';
      const leaf = addField ? leafOf(addField, op, emptyValue(op)) : null;
      setDraft(leaf ? { ...root, children: [...root.children, leaf] } : root);
      setAdded(leaf?.id);
      setProblems(placed);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[min(44rem,calc(100vw-2rem))] gap-0 overflow-y-auto p-0">
        <div className="grid gap-1 border-b border-border px-5 py-4">
          <SheetTitle className="text-[0.9375rem] text-[var(--color-royal-blue)]">Advanced filter</SheetTitle>
          <SheetDescription className="text-[0.8125rem]">
            Conditions on any field, in groups — all of them, or any of them. The columns’ funnels
            change the same filter.
          </SheetDescription>
        </div>
        <form
          className="grid gap-4 px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault();
            onApply(draft);
            onOpenChange(false);
          }}
        >
          <CriteriaBuilder
            root={draft}
            catalogue={catalogue}
            words={words}
            placed={problems}
            accounts={accounts}
            focusId={added ?? focusId}
            onChange={(next) => {
              setProblems((held) => forgetChanged(held, draft, next));
              setDraft(next);
            }}
          />
          <div className="flex gap-2 border-t border-border pt-3">
            <Button type="submit" className="h-8 px-3">
              Apply
            </Button>
            <Button type="button" variant="outline" className="h-8 px-3" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
